<script>
// Helpers
import {authComputed, layoutComputed, onlineMethods} from "@/state/helpers";
import {EventBus} from "@/helpers/event-bus";
import { db } from "@/plugins/firebase";
import {version} from "../../package"

// Libs
import i18n from "../i18n";
import moment from "moment";

/**
 * Horizontal-topbar component
 */
export default {
  props: {
    type: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...layoutComputed,
    ...authComputed
  },
  data() {
    return {
      appVersion: version,
      languages: [
        {
          flag: require("@/assets/images/flags/us.jpg"),
          language: "en",
          title: "English",
        },
        {
          flag: require("@/assets/images/flags/french.jpg"),
          language: "fr",
          title: "French",
        }
      ],
      listener: null,
      operators: [],
      current_language: i18n.locale,
      text: null,
      flag: null,
      value: null,
    };
  },
  beforeDestroy() {
    const ref = db.ref('admin-status');
    ref.off("value", this.listener);
  },
  created() {
    this.loadUsers();
  },
  mounted() {
    this.value = this.languages.find((x) => x.language === i18n.locale);
    moment.locale(this.value.language);
    this.text = this.value.title;
    this.flag = this.value.flag;
  },
  methods: {
    ...onlineMethods,

    toggleRightSidebar() {
      this.$parent.toggleRightSidebar();
    },

    toggleMenu() {
      let element = document.getElementById("topnav-menu-content");
      element.classList.toggle("show");
    },

    getInitials (fullName) {
      return fullName.split(' ').map(name => name[0]).join('').toUpperCase();
    },

    initFullScreen() {
      document.body.classList.toggle("fullscreen-enable");
      if (
        !document.fullscreenElement &&
        /* alternative standard method */ !document.mozFullScreenElement &&
        !document.webkitFullscreenElement
      ) {
        // current working methods
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(
            Element.ALLOW_KEYBOARD_INPUT
          );
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },

    setLanguage(locale, country, flag) {
      this.current_language = locale;
      this.text = country;
      this.flag = flag;
      i18n.locale = locale;
      moment.locale(locale);
    },

    loadUsers() {
      const ref = db.ref('admin-status');
      this.listener = ref.on("value", (snapshot) => {
        const val = snapshot.val();
        const op = [];
        for (const key in val) {
          op.push({
            name: val[key].name,
            id: key,
            role: val[key].role,
            initials: this.getInitials(val[key].name),
            time: val[key].timestamp,
            section: val[key].section,
            path: val[key].path
          });
        }

        op.sort((a, b) =>  parseInt(a.time) - parseInt(b.time));
        this.operators = op;
        this.setOnlineUsers(op);
        EventBus.$emit('onlineUsers', op);
      });
    }
  },
  watch: {
    width: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case "boxed":
              document.body.setAttribute("data-layout-size", "boxed");
              break;
            case "fluid":
              document.body.setAttribute("data-layout-mode", "fluid");
              document.body.removeAttribute("data-layout-size");
              break;
            case "scrollable":
              document.body.setAttribute("data-layout-scrollable", "true");
              document.body.removeAttribute("data-layout-mode");
              document.body.removeAttribute("data-layout-size");
              break;
            default:
              document.body.setAttribute("data-layout-mode", "fluid");
              break;
          }
        }
      },
    },
  },
};
</script>

<template>
  <header id="page-topbar">
    <div class="navbar-header">
      <div class="d-flex align-items-center">
        <!-- LOGO -->
        <div class="navbar-brand-box">
          <router-link to="/" class="logo logo-light">
            <span class="logo-lg">
              <img src="@/assets/images/logo-light.png" alt height="40" />
            </span>
          </router-link>
        </div>

        <!-- toggle menu not implemented in horz mode -->
        <button
          id="toggle"
          type="button"
          class="btn btn-sm me-2 font-size-16 d-lg-none header-item"
          @click="toggleMenu"
        >
          <i class="fa fa-fw fa-bars"></i>
        </button>

        <span class="badge rounded-pill bg-info font-size-13">{{ appVersion }}</span>
      </div>

      <div class="d-flex">
        <!-- online users -->
        <b-dropdown
            class="d-none d-lg-inline-block noti-icon"
            menu-class="dropdown-menu-lg dropdown-menu-end"
            right
            toggle-class="header-item"
            variant="black"
        >
          <template v-slot:button-content>
            <i class="bx bxs-circle font-size-12 text-success"></i> <b>{{ operators.length }}</b> Online
          </template>

          <div class="px-lg-2">
            <div class="row no-gutters">
              <div class="" v-for="(operator, index) in operators" :key="index">
                <div class="media">
                  <div class="avatar-xs">
                    <span
                        class="avatar-title rounded-circle font-size-14 bg-dark text-white"
                    >{{ operator.initials || '-'}}</span>
                  </div>
                  <div class="media-body ms-2">
                    <div class="d-flex align-items-center">
                      <p class="p-0 m-0">
                        <span
                            class="text-muted fw-bold font-size-13"
                        >{{ operator.name || '-' }}<br><small class="fw-light">{{ operator.time | moment('lll') }}</small></span><br>
                        <span
                            v-if="['user', 'profile', 'ticket', 'media', 'report'].includes(operator.section)"
                            class="rounded ps-1 pe-1 text-white font-size-11"
                            :class="{
                        'bg-success': `${operator.section}` === 'user',
                        'bg-warning': `${operator.section}` === 'profile',
                        'bg-danger': `${operator.section}` === 'ticket',
                        'bg-info': `${operator.section}` === 'media',
                        'bg-pink': `${operator.section}` === 'report',
                      }"
                        >{{ operator.path || '-' }}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-dropdown>

        <!-- Me Profile -->
        <b-dropdown right variant="black" toggle-class="header-item">
          <template v-slot:button-content>
            <img
                v-if="getUser.photoURL"
                :src="getUser.photoURL"
                alt=""
                class="rounded-circle header-profile-user"
            />
            <span v-else class="rounded-circle header-profile-user bg-success text-white font-size-13 text-center" style="line-height: 2.5rem;" >{{ getInitials(getUser.displayName) }}</span>

            <span class="d-none d-xl-inline-block ms-1">{{
                getUser.displayName
              }}</span>
            <i class="mdi mdi-chevron-down d-none d-xl-inline-block"></i>
          </template>
          <!-- item-->
          <b-dropdown-item>
              <i class="bx bx-user font-size-16 align-middle me-1"></i>
              #ID {{ getUser.uid }}
          </b-dropdown-item>
          <b-dropdown-item class="d-block" href="javascript: void(0);">
            <i class="bx bx-wrench font-size-16 align-middle me-1"></i>
            {{ getUser.email }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <a href="/logout" class="dropdown-item text-danger">
            <i
                class="bx bx-power-off font-size-16 align-middle me-1 text-danger"
            ></i>
            {{ $t("logout") }}
          </a>
        </b-dropdown>

        <!-- Languages -->
        <b-dropdown variant="white" right toggle-class="header-item" menu-class="dropdown-menu-end">
          <template v-slot:button-content>
            <img class :src="flag" alt="Header Language" height="16" />
            {{ text }}
          </template>
          <b-dropdown-item
            class="notify-item"
            v-for="(entry, i) in languages"
            :key="`Lang${i}`"
            :value="entry"
            @click="setLanguage(entry.language, entry.title, entry.flag)"
            :class="{ active: current_language === entry.language }"
          >
            <img
              :src="`${entry.flag}`"
              alt="user-image"
              class="me-1"
              height="12"
            />
            <span class="align-middle">{{ entry.title }}</span>
          </b-dropdown-item>
        </b-dropdown>

        <!-- FullScreen -->
        <div class="dropdown d-none d-lg-inline-block ms-1">
          <button
            type="button"
            class="btn header-item noti-icon"
            @click="initFullScreen"
          >
            <i class="bx bx-fullscreen"></i>
          </button>
        </div>

        <!-- Right Bar toggle -->
        <div class="dropdown d-inline-block">
          <button
            type="button"
            class="btn header-item noti-icon right-bar-toggle toggle-right"
            @click="toggleRightSidebar"
          >
            <i class="bx bx-cog bx-spin toggle-right"></i>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>